import { USER_ROLES } from 'src/common/constants';

export interface ResolveChatRecipientParams {
  thread: any;
  chatUserInformation: any;
  currentUserId: string;
  roleName: string;
}

export interface BuildChatMessagePayloadParams {
  thread: any;
  messageText: string;
  recipientUserId: string | null;
  imageUrl: string | null;
  senderUserId: string;
}

export interface ChatMessagePayload {
  SentBy: string;
  SentTo: string | null;
  ReferenceId: string;
  ReferenceType: string;
  Message: string;
  FileName: string | undefined;
  FileURL: string | null;
  TicketNumber: string;
  ActionUrl: string;
}

export interface VideoChatInviteData {
  SentBy: string;
  SentTo: string;
  ChannelName: string;
  ActionUrl: string;
  PropertyName: string;
  SuiteName: string;
}

/* legacy/workaround: the API returns this placeholder GUID as a "user" id for
 * suites with no home owner assigned — treat it as "no recipient". */
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export const resolveChatRecipientUserId = ({
  thread,
  chatUserInformation,
  currentUserId,
  roleName,
}: ResolveChatRecipientParams): string | null => {
  const isDeveloper: boolean = roleName === USER_ROLES.PROPERTY_DEVELOPER;
  const isSuiteThread: boolean = thread.referenceType === 'Suite';

  if (isSuiteThread && isDeveloper) {
    return chatUserInformation.homeOwner?.userId || null;
  }

  if (!isDeveloper) {
    return chatUserInformation.propertyManager?.userId || null;
  }

  const currentUserIsReporter: boolean =
    chatUserInformation.reporter?.userId === currentUserId;

  if (currentUserIsReporter) {
    return chatUserInformation.homeOwner?.userId || null;
  }

  return chatUserInformation.reporter?.userId || null;
};

export const chatThreadHasNoValidRecipient = (
  thread: any,
  roleName: string,
  recipientUserId: string | null
): boolean => {
  const suiteThreadHasNoHomeOwner: boolean =
    thread.referenceType === 'Suite' &&
    roleName === USER_ROLES.PROPERTY_DEVELOPER &&
    !recipientUserId;

  const recipientIsPlaceholderGuid: boolean = recipientUserId === EMPTY_GUID;

  return suiteThreadHasNoHomeOwner || recipientIsPlaceholderGuid;
};

export const buildVideoChatChannelName = (thread: any): string => {
  const ticketIdWithoutSpaces: string =
    thread.ticketId?.replace(/\s/g, '') || '';

  return `channel${ticketIdWithoutSpaces}`;
};

export interface BuildVideoChatInviteParams {
  thread: any;
  message: any;
  channelName: string;
}

export const buildVideoChatInviteData = ({
  thread,
  message,
  channelName,
}: BuildVideoChatInviteParams): VideoChatInviteData => {
  const senderNameWithoutSpaces: string = message.sentByName.replace(
    /\s/g,
    ''
  );

  return {
    SentBy: message.sentTo,
    SentTo: message.sentBy,
    ChannelName: channelName,
    ActionUrl: `app/videoChat/chatId=${channelName}/${senderNameWithoutSpaces}`,
    PropertyName: thread.propertyName,
    SuiteName: thread.suiteName,
  };
};

export const buildChatMessagePayload = ({
  thread,
  messageText,
  recipientUserId,
  imageUrl,
  senderUserId,
}: BuildChatMessagePayloadParams): ChatMessagePayload => {
  const uploadedFileName: string | undefined = imageUrl
    ? imageUrl.split('.com/')[1]
    : undefined;

  return {
    SentBy: senderUserId,
    SentTo: recipientUserId,
    ReferenceId: thread.referenceId,
    ReferenceType: thread.referenceType,
    Message: messageText,
    FileName: uploadedFileName,
    FileURL: imageUrl,
    TicketNumber: thread.ticketNumber,
    ActionUrl: 'app/inbox/' + thread.referenceId,
  };
};